import { useEffect, useState } from "react";
import { api } from "../lib/api";

const CUSTOM_SENTINEL = "__custom__";

/**
 * CategorySelect — dropdown of service categories plus a "+ New category…"
 * affordance that swaps the select for a text input so practitioners can
 * coin their own categories on the fly. Used by Profile (create + edit)
 * and Marketplace (edit + filter).
 *
 * Props:
 *   - value             current category string
 *   - onChange(newVal)  fires whenever the user picks an existing one or
 *                       types a new one (debounced)
 *   - allowAll          if true, prepends "All" (used by the marketplace filter)
 *   - testId            data-testid for the underlying control
 *   - className         passthrough Tailwind class
 */
export default function CategorySelect({
  value, onChange, allowAll = false, testId, className = "",
}) {
  const [categories, setCategories] = useState([]);
  const [mode, setMode] = useState("pick"); // pick | type
  const [customDraft, setCustomDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/services/categories")
      .then(({ data }) => { if (!cancelled) setCategories(data.categories || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const baseClass = `bg-input border border-secondary/20 p-3 rounded-sm ${className}`;

  if (mode === "type") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          maxLength={40}
          value={customDraft}
          onChange={(e) => {
            setCustomDraft(e.target.value);
            onChange(e.target.value);
          }}
          onBlur={() => {
            if (!customDraft.trim()) {
              setMode("pick");
              onChange(value || (allowAll ? "All" : (categories[0] || "Readings")));
            }
          }}
          placeholder="New category name"
          className={baseClass}
          data-testid={testId ? `${testId}-custom-input` : undefined}
        />
        <button
          type="button"
          onClick={() => {
            setMode("pick");
            setCustomDraft("");
            onChange(value || (allowAll ? "All" : (categories[0] || "Readings")));
          }}
          className="text-xs text-foreground/55 hover:text-foreground uppercase tracking-wider"
          data-testid={testId ? `${testId}-custom-cancel` : undefined}
        >
          Cancel
        </button>
      </div>
    );
  }

  // Build the option set, ensuring the current value is included even if it
  // isn't in the fetched list yet (e.g., the practitioner just typed it).
  const opts = allowAll ? ["All", ...categories] : categories.slice();
  if (value && !opts.includes(value)) opts.push(value);

  return (
    <select
      value={value || (allowAll ? "All" : "")}
      onChange={(e) => {
        const v = e.target.value;
        if (v === CUSTOM_SENTINEL) {
          setMode("type");
          setCustomDraft("");
          return;
        }
        onChange(v);
      }}
      className={baseClass}
      data-testid={testId}
    >
      {opts.map((c) => <option key={c} value={c}>{c}</option>)}
      <option value={CUSTOM_SENTINEL}>+ New category…</option>
    </select>
  );
}
